import { store } from 'state/_store'
import { getSettings } from 'state/Settings'
import platform from 'utils/platform'
import StarPrinters from './StarPrinters'
import Alerts from './Alerts'
import Head from 'components/layout/Head'

const SOUND_ID = 'alertSound'

/** @returns {?HTMLAudioElement} */
const querySoundEl = () => document.querySelector(`audio#${SOUND_ID}`)

/**
 * The Audio element used for alerts, with the current position always set to 0.
 * @param settings {TabletOptions|null} If given, will reconfigure the element as needed.
 * @return HTMLAudioElement|void
 */
export const getSoundEl = (settings = null) => {
	let sound = querySoundEl()
	if (!sound) {
		sound         = document.createElement('audio')
		sound.id      = SOUND_ID
		sound.preload = 'auto'
		sound.volume  = 1 //being on the safe side
		document.querySelector('body').appendChild(sound) //let's call this "DOM caching" :))
	}
	if (settings) {
		sound.src  = settings.selectedSoundFile()
		sound.loop = settings.newOrderAlert && settings.loopSound //loop is disabled without alerts - no way to stop it lol
	}
	sound.currentTime = 0
	return sound
}

getSoundEl.init = settings => {
	if (!querySoundEl) { //avoids to reinitialize/reset the sound on re-renders
		getSoundEl(settings)
	}
}

/**
 * Creates all the configured notifications for a single order - alert, sound, receipt printing -, respecting all notification settings.
 * @param order {Order}
 * @param options {{ [force]: boolean, [skipSound]: boolean, [skipAlert]: boolean }} Force will ignore {@link TabletOptions.newOrderWhen}. The skip options are used in specific cases (such as considering the state when a push is received).
 * @param testSettings {TabletOptions|object} Overrides the current notification settings (and also disables printing). It's used for testing alerts in the Settings screen - please DON'T use this to override settings in other places!
 * @todo maybe move this testSettings param into a different testNotifyOrder() function, to make it extra-clear it's not meant to be used in normal calls?
 */
export const notifyOrder = (order, options = {}, testSettings = null) => {
	const settings = getSettings(store.getState())
	if (!options.force && !order.shouldNotify(settings.newOrderWhen)) {
		return
	}

	//decides about printing or not, and overrides `settings` with any `testSettings` given
	if (testSettings) {
		Object.assign(settings, testSettings || {})
		console.warn('Skip printing since this is a test notification.', testSettings)
	} else {
		StarPrinters.print(order, true)
	}

	if (!options.skipSound && settings.newOrderSound) {
		getSoundEl(settings).play() //don't try to load this outside of the function - you'll face a race condition
	}

	if (!options.skipAlert && settings.newOrderAlert) {
		Alerts.order(order, settings.flashScreen)
		blinkTab(true)
	}
}

/**
 * Creates a browser tab blinking notification
 * @param status {boolean} Should we start or stop the tab blinking?
 */
export const blinkTab = (status) => {
	if (platform.isNative()) {
		return
	}

	if (!status) {
		if (window.TAB_NOTIF) {
			window.TAB_NOTIF = false
			window.clearInterval(window.INTERVAL_FAVICON_WHITE)
			window.clearInterval(window.INTERVAL_FAVICON_BLACK)
			document.title = window.BACKUP_TITLE //can't use Head.makeTitle() as it breaks state consistency when called from a reducer
			if (window.BACKUP_ICONS) {
				//a rare race-condition between multiple orders might cause this to be undefined? see MANAGER-4A
				replaceFavicon(...window.BACKUP_ICONS)
			}
		}
		return
	}

	if (window.TAB_NOTIF) {
		return
	}

	//TODO: this icon backup scheme could be replaced by simply changing the color suffix back and forth?
	const favicon16 	  = document.querySelector("link[rel~='icon'][sizes~='16x16']")
	const favicon32 	  = document.querySelector("link[rel~='icon'][sizes~='32x32']")
	const blackFavicons = [favicon('16', 'black'), favicon('32', 'black')]
	const whiteFavicons = [favicon('16', 'white'), favicon('32', 'white')]

	//TODO: this backup mechanism will break if the user is able to change page while the notification is happening - if that's needed, we'll need to rely on state (maybe sniff in the alerts?) to define the notification in title (or drop it)
	/** Avoids adding new notifications in tab if that's already active */
	window.TAB_NOTIF    = true
	window.BACKUP_ICONS = [favicon16.href, favicon32.href]
	window.BACKUP_TITLE = document.title

	document.title      = Head.makeTitle(null, true)
	replaceFavicon(...blackFavicons)

	const loopTime = 1500 //same time from the CSS flashing animation, but they don't really sync...?
	window.INTERVAL_FAVICON_WHITE = window.setInterval(() => {
		replaceFavicon(...whiteFavicons)
		window.INTERVAL_FAVICON_BLACK = window.setTimeout(() => replaceFavicon(...blackFavicons), loopTime / 2)
	}, loopTime)
}

/**
 * @param size {'16'|'32'}
 * @param color {'black'|'white'}
 * @return {string}
 */
const favicon = (size, color) => {
	return Head.mediaPath(`favicon${size}-${color}`, 'png', { folder: 'favicon' })
}

const replaceFavicon = (link16, link32 = link16) => {
	document.querySelector("link[rel~='icon'][sizes~='16x16']").href = link16
	document.querySelector("link[rel~='icon'][sizes~='32x32']").href = link32
}
